import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PinInput from "./PinInput";
import { updateEmail, validateOpt, getApiResponsePayload, getResponseCode } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

const StatusBanner = ({ user, onAction, isLoadingData = false, informational = null }) => {
  const { t } = useTranslation("mobile");
  const [showPinInput, setShowPinInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  if (informational && (informational.title || informational.message)) {
    const showInfoButton =
      informational.buttonText && typeof informational.onButtonClick === "function";
    return (
      <div className="status-banner status-banner--in-flow">
        <div className="status-banner-content">
          <div className="status-banner-text">
            {informational.title ? (
              <h3 className="status-banner-title">{informational.title}</h3>
            ) : null}
            {informational.message ? (
              <p className="status-banner-message">{informational.message}</p>
            ) : null}
          </div>
          {showInfoButton ? (
            <button
              type="button"
              className="status-banner-button"
              onClick={informational.onButtonClick}
            >
              {informational.buttonIcon ? (
                <i className={`fa-solid ${informational.buttonIcon}`} />
              ) : null}
              <span>{informational.buttonText}</span>
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!user && !isLoadingData) return null;

  // Determine the highest priority status to show
  const getStatusInfo = () => {
    // Check profile completion
    const profileCompleted = user.PROFILE_STATUS === 1;
    if (!profileCompleted) {
      return {
        title: t("status_banner_complete_profile_title"),
        message: t("status_banner_complete_profile_message"),
        buttonText: t("status_banner_unlock"),
        icon: "fa-unlock", // Unlocked padlock icon to match the design
        priority: 1
      };
    }

    // Check email verification - IS_EMAIL_VERIFIED === 0 means not verified
    const isEmailNotVerified = user.user_information?.IS_EMAIL_VERIFIED === 0 || 
                                user.userInformation?.IS_EMAIL_VERIFIED === 0;
    if (isEmailNotVerified) {
      return {
        title: t("status_banner_verify_email_title"),
        message: t("status_banner_verify_email_message"),
        buttonText: t("status_banner_unlock"),
        icon: "fa-unlock", // Unlocked padlock icon to match the design
        priority: 2,
        needsEmailVerification: true
      };
    }

    // Check PIN reset requirement
    const requiredToResetPin = user.REQUIRED_TO_RESET_PIN === 1 || user.requiredToResetPin === true;
    if (requiredToResetPin) {
      return {
        title: t("status_banner_change_pin_title"),
        message: t("status_banner_change_pin_message"),
        buttonText: t("status_banner_change_pin"),
        icon: "fa-key",
        priority: 3
      };
    }

    // Check KYC status (backend enum mapping):
    // -3 UPDATED_KYC, -2 HAS_KYC_ERROR, -1 HAS__APPROVED_ERROR,
    //  0 PENDING, 1 APPROVED, 2 VERIFIED, 3 VERIFIED_NOT_CHECKED
    const kycStatus = user.user_information?.IS_KYC_VERIFIED ??
                      user.userInformation?.IS_KYC_VERIFIED ??
                      user.user_information?.isKycVerified;

    // Needs ADMIN APPROVAL (registration/KYC approval step)
    if (kycStatus === 0 || kycStatus === -1) {
      return {
        title: t("status_banner_account_approval_title"),
        message: t("status_banner_account_approval_message"),
        buttonText: t("status_banner_view_status"),
        icon: "fa-id-card",
        priority: 4
      };
    }

    // Needs FINAL VERIFICATION (after approval – user must complete/verify KYC)
    if (kycStatus === 1 || kycStatus === -3 || kycStatus === -2) {
      return {
        title: t("status_banner_account_verification_title"),
        message: t("status_banner_account_verification_message"),
        buttonText: t("status_banner_verify_account"),
        icon: "fa-id-card",
        priority: 4
      };
    }

    // Fully verified & active: no banner should be shown
    if (kycStatus === 2 || kycStatus === 3) {
      return null;
    }

    // Default: no specific KYC state, treat as no banner
    return null;
  };

  const statusInfo = isLoadingData
    ? {
        title: "",
        message: "",
        buttonText: "",
        icon: "fa-unlock",
      }
    : getStatusInfo();
  if (!statusInfo) return null;

  const handleUnlockClick = async () => {
    if (isLoadingData) return;
    // If email verification is needed and PIN input is not shown yet
    if (statusInfo.needsEmailVerification && !showPinInput) {
      setLoading(true);
      setError("");
      
      try {
        // Get email from user object or sessionStorage
        const email = user?.user_information?.EMAIL || 
                     user?.userInformation?.EMAIL || 
                     sessionStorage.getItem("userEmail") ||
                     sessionStorage.getItem("email") ||
                     localStorage.getItem("userEmail") ||
                     localStorage.getItem("email") ||
                     "";

        if (!email) {
          setError(t("status_banner_email_not_found"));
          setLoading(false);
          return;
        }

        // Call update-email API
        const response = await updateEmail(email);
        const result = response?.decryptedData || response?.data;

        if (result?.CODE === AppConstants.responseOk) {
          setEmailSent(true);
          setShowPinInput(true);
          setError("");
        } else {
          setError(result?.MESSAGE || result?.message || t("status_banner_otp_failed"));
        }
      } catch (err) {
        setError(err.message || t("status_banner_otp_failed"));
      } finally {
        setLoading(false);
      }
    } else if (onAction) {
      onAction(statusInfo);
    }
  };

  const handlePinChange = (code) => {
    setVerificationCode(code);
    if (code.length === 5) {
      handleVerifyCode(code);
    }
  };

  const handleVerifyCode = async (code) => {
    if (!code || code.length !== 5) {
      setError(t("status_banner_invalid_code_length"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Call validate-opt API
      const response = await validateOpt(code);
      const result = getApiResponsePayload(response);
      const code = getResponseCode(response);

      if (code === AppConstants.responseOk || code === AppConstants.messagesOK) {
        setError("");
        setShowPinInput(false);
        setVerificationCode("");
        setEmailSent(false);
        
        // Refresh the page or update user state
        if (onAction) {
          onAction({ ...statusInfo, verified: true });
        }
        // Optionally reload to refresh user data
        window.location.reload();
      } else {
        setError(result?.MESSAGE || result?.message || t("status_banner_otp_invalid"));
        setVerificationCode("");
      }
    } catch (err) {
      setError(err.message || t("status_banner_otp_verify_failed"));
      setVerificationCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={`status-banner ${statusInfo.isCompleted ? 'status-banner--completed' : ''}`}>
        <div className="status-banner-content">
          <div className="status-banner-text">
            <h3 className="status-banner-title">
              {isLoadingData ? <span className="status-banner-skeleton-line status-banner-skeleton-line--title" /> : statusInfo.title}
            </h3>
            <p className="status-banner-message">
              {isLoadingData ? <span className="status-banner-skeleton-line status-banner-skeleton-line--message" /> : statusInfo.message}
            </p>
            
            {!showPinInput && error && (
              <p className="status-banner-error">{error}</p>
            )}
          </div>
          <button 
            className="status-banner-button"
            onClick={handleUnlockClick}
            type="button"
            disabled={loading || showPinInput || isLoadingData}
          >
            {isLoadingData ? (
              <span className="status-banner-button-skeleton" />
            ) : loading ? (
              <span>{t("status_banner_loading")}</span>
            ) : (
              <>
                <i className={`fa-solid ${statusInfo.icon}`}></i>
                <span>{statusInfo.buttonText}</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Verification Overlay - appears like in InformationAccount.js */}
      {showPinInput && statusInfo.needsEmailVerification && (
        <div className="verification-overlay">
          <div className="verification-card">
            <h1 className="verification-title">Verify Your Email</h1>
            <p className="verification-subtitle">
              {emailSent 
                ? "Verification code sent to your email. Please enter the code below."
                : "Please enter the verification code sent to your email."
              }
            </p>

            <PinInput length={5} onChange={handlePinChange} />

            {error && (
              <p className="verification-error">{error}</p>
            )}

            <button
              className="verification-button"
              onClick={() => handleVerifyCode(verificationCode)}
              disabled={loading || verificationCode.length !== 5}
            >
              {loading ? t("status_banner_verify_overlay_verifying") : t("status_banner_verify_overlay_button")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default StatusBanner;
